import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import PartySection from './PartySection';
import FriendsSection from './FriendsSection';
import type { UsePartyResult } from '../../hooks/useParty';
import { useDockDrag } from './useDockDrag';
import styles from './HubPartyDock.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// HubPartyDock — ícone "👥" na barra compacta do Hub (ao lado de Equipamento
// / Mochila / Ficha). Abre uma janela ENCAIXADA com abas Equipa/Amigos —
// mesma mecânica de arrastar/fechar das outras janelas do Hub.
//
// Reaproveita PartySection e FriendsSection tal-e-qual (mesmos componentes do
// dashboard fora do mapa); PartySection recebe `variant="hub"` para trocar o
// botão "Iniciar" instantâneo pela dica do Portal do Guardião — ver
// HubMap.tsx para a detecção de "grupo reunido no portal".
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  token: string;
  dockTop: number;
  partyState: UsePartyResult;
}

export default function HubPartyDock({ token, dockTop, partyState }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'party' | 'friends'>('party');
  const dragCtl = useDockDrag(open);

  const hasInvite = !!partyState.invite;

  return (
    <>
      <button
        className={`${styles.compactIcon} ${open ? styles.compactIconOn : ''}`}
        onClick={() => setOpen(v => !v)}
        title={t('hub.friends.title' as any)}
        aria-label={t('hub.friends.title' as any)}
      >
        👥
        {hasInvite && !open && (
          <span className={styles.compactBadge} title={t('hub.friends.hasInvite' as any)} />
        )}
      </button>

      {open && (
        <div
          className={styles.dock}
          data-dock-window=""
          style={{ '--dock-top': `${dockTop}px`, ...dragCtl.style } as React.CSSProperties}
        >
          <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t('hub.friends.title' as any)}>
            <header className={styles.modalHeader} onPointerDown={dragCtl.onHeaderPointerDown}>
              <div className={styles.headerLeft}>
                <span style={{ fontSize: 18 }}>👥</span>
                <h2 className={styles.modalTitle}>{t('hub.friends.title' as any)}</h2>
              </div>
              <button className={styles.closeBtn} onClick={() => setOpen(false)} aria-label={t('common.close')}>✕</button>
            </header>

            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${tab === 'party' ? styles.tabOn : ''}`}
                onClick={() => setTab('party')}
              >
                ⚔ {t('party.title')}
                {hasInvite && <span className={styles.tabDot} />}
              </button>
              <button
                className={`${styles.tab} ${tab === 'friends' ? styles.tabOn : ''}`}
                onClick={() => setTab('friends')}
              >
                👤 {t('friends.title')}
              </button>
            </div>

            <div className={styles.modalBody}>
              {tab === 'party'
                ? <PartySection token={token} variant="hub" partyState={partyState} />
                : <FriendsSection token={token} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
